import { useEffect, useRef } from 'react';
import { useBindToChannelLazily } from './useBindToChannelLazily';
import type { GroupId } from 'src/services/groupService/models/group.model';
import type { UserId } from 'src/services/userService/models/user.model';
import type {
  Channel,
  ChannelEvent,
  ChannelEventData,
  GroupChannel,
  NotificationChannel,
} from 'src/services/notificationService/channelsService/types';

function useBindToChannelEvents<
  C extends Channel,
  E extends ChannelEvent<C>,
  Data extends ChannelEventData<C, E> = ChannelEventData<C, E>
>(
  channel: Channel | null,
  listener: (args: { event: E; data: Data }) => void,
  events: E[],
  options?: { enforceSingleListener?: boolean }
) {
  const bindEventListener = useBindToChannelLazily();

  // Stabilize listener identity. Callers overwhelmingly pass inline arrows;
  // putting the function itself in the effect deps would force unbind→rebind
  // on every parent render and race the async bind path. Mirrors the pattern
  // in useBindToChannelEvent.
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  // The events array is also typically inline. Read it via a ref inside the
  // effect, but key the effect on a content-derived string so reruns happen
  // only when the actual set of event names changes. Sort makes the key
  // order-independent — caller intent is "bind to these events", not
  // "bind in this order".
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const eventsKey = [...events].sort().join('|');

  useEffect(() => {
    if (channel == null) {
      // we allow channel to be nullish to make it easier for the calling component - ideally we would never need to do this
      return;
    }

    const boundEvents = eventsRef.current.map((eventName) => {
      return bindEventListener(channel, {
        // in general, we want to enforce single listener for post related events, since we use it at the fetching level
        enforceSingleListener: options?.enforceSingleListener ?? false,
        eventName,
        listener(data) {
          listenerRef.current({ event: eventName, data: data as Data });
        },
      });
    });

    // Unbind on every cleanup (effect re-run AND unmount). The previous
    // componentWillUnmount gate skipped unbind on re-run, which orphaned
    // listeners under their old channel key whenever the channel prop changed.
    return () => {
      boundEvents.forEach((b) => {
        b.unbind();
      });
    };
  }, [bindEventListener, channel, eventsKey, options?.enforceSingleListener]);
}

export function useBindToNotificationEvents<
  C extends NotificationChannel,
  E extends ChannelEvent<C>,
  Data extends ChannelEventData<C, E> = ChannelEventData<C, E>
>(
  /**
   * todo: ideally the caller always provides an id
   */
  userId: UserId | null,
  listener: (args: { event: E; data: Data }) => void,
  events: E[],
  options?: { enforceSingleListener?: boolean }
) {
  return useBindToChannelEvents<C, E, Data>(
    userId != null ? `private-notifications-${userId}` : null,
    listener,
    events,
    options
  );
}

export function useBindToGroupEvents<
  C extends GroupChannel,
  E extends ChannelEvent<C>,
  Data extends ChannelEventData<C, E> = ChannelEventData<C, E>
>(
  /**
   * todo: force the caller to always provide an id
   */
  groupId: GroupId | null | undefined,
  listener: (args: { event: E; data: Data }) => void,
  events: E[],
  options?: { enforceSingleListener?: boolean }
) {
  return useBindToChannelEvents<C, E, Data>(
    groupId != null ? `private-group-${groupId}` : null,
    listener,
    events,
    options
  );
}
